import { useMemo, createContext, useContext } from 'react';

import { ReactFlow, Background, Node, Edge, Position, Handle, NodeProps } from '@xyflow/react';

import '@xyflow/react/dist/style.css';

import { STAGE_CONFIGS, StageKey } from '../utils';
import { DeletedBreakdownPopover, PopoverLink, SkippedBreakdownPopover } from './breakdown-popovers';
import { FileHistoryStats } from '@/api/queries/useGetFileHistoryStats';
import { Icon } from '@/new/components/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';

interface UploadFlowChartProps {
  matterId: string;
  allDocumentsCaseId?: string;
  stats: FileHistoryStats;
  onViewSkipped?: () => void;
  onViewDeleted?: () => void;
  linksEnabled?: boolean;
}

type FlowNodeData = {
  label: string;
  count: number;
  color: string;
  stageKey?: StageKey;
  isStart?: boolean;
  isEnd?: boolean;
};

type FlowNode = Node<FlowNodeData, 'flowNode'>;

const NODE_WIDTH = 140;
const NODE_HEIGHT = 36;
const EDGE_COLOR = '#e5e7eb';

// Context for passing data to custom nodes
interface FlowChartContextValue {
  matterId: string;
  allDocumentsCaseId?: string;
  stats: FileHistoryStats;
  onViewSkipped?: () => void;
  onViewDeleted?: () => void;
  linksEnabled?: boolean;
}

const FlowChartContext = createContext<FlowChartContextValue | null>(null);

function ExplainerPopoverContent({
  config,
  matterId,
  allDocumentsCaseId,
  linksEnabled = true,
}: {
  config: (typeof STAGE_CONFIGS)[StageKey];
  matterId: string;
  allDocumentsCaseId?: string;
  linksEnabled?: boolean;
}) {
  return (
    <PopoverContent className="w-56" align="center">
      <p className="text-xs text-gray-600">{config.description}</p>
      {config.link && linksEnabled && allDocumentsCaseId && (
        <div className="mt-3 border-t border-gray-100 pt-2">
          <PopoverLink to={config.link(matterId, allDocumentsCaseId)} newTab>
            View files
          </PopoverLink>
        </div>
      )}
    </PopoverContent>
  );
}

function FlowChartNode({ data }: NodeProps<FlowNode>) {
  const ctx = useContext(FlowChartContext);
  const { label, count, color, isStart, isEnd, stageKey } = data;

  const config = stageKey ? STAGE_CONFIGS[stageKey] : null;

  const nodeContent = (
    <div
      className="flex cursor-pointer items-center justify-center rounded-md border-2 bg-white px-3 py-2 text-xs font-medium text-gray-700 duration-150 hover:bg-gray-50"
      style={{ borderColor: color, minWidth: NODE_WIDTH, height: NODE_HEIGHT }}
    >
      {!isStart && <Handle type="target" position={Position.Left} />}
      <span className="truncate font-medium">
        {count.toLocaleString()} {label}
      </span>
      {!isEnd && <Handle type="source" position={Position.Right} />}
    </div>
  );

  if (!ctx || !config || !stageKey) {
    return nodeContent;
  }

  // Skipped - show breakdown popover
  if (stageKey === 'skipped') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="block">
            {nodeContent}
          </button>
        </PopoverTrigger>
        <SkippedBreakdownPopover stats={ctx.stats} onViewAll={ctx.onViewSkipped} />
      </Popover>
    );
  }

  // Deleted - show breakdown popover
  if (stageKey === 'deleted') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="block">
            {nodeContent}
          </button>
        </PopoverTrigger>
        <DeletedBreakdownPopover stats={ctx.stats} onViewAll={ctx.onViewDeleted} />
      </Popover>
    );
  }

  // All other nodes - show explainer popover (with optional link)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="block">
          {nodeContent}
        </button>
      </PopoverTrigger>
      <ExplainerPopoverContent
        config={config}
        matterId={ctx.matterId}
        allDocumentsCaseId={ctx.allDocumentsCaseId}
        linksEnabled={ctx.linksEnabled}
      />
    </Popover>
  );
}

const nodeTypes = {
  flowNode: FlowChartNode,
};

interface FlowData {
  nodes: FlowNode[];
  edges: Edge[];
}

function buildFlowData(stats: FileHistoryStats): FlowData {
  const nodes: FlowNode[] = [];
  const edges: Edge[] = [];

  const COL_SPACING = 200;
  const ROW_SPACING = 55;

  const {
    totalAttempted,
    totalSuccessful: totalUploaded,
    totalFailed: totalSkipped,
    deletionsByReason,
    processed,
    notProcessed,
    stagedFiles,
    submittedFiles,
  } = stats;

  // Column 1 (leftmost): Attempted
  const attemptedId = 'attempted';
  nodes.push({
    id: attemptedId,
    type: 'flowNode',
    position: { x: 0, y: 0 },
    data: {
      label: 'Attempted',
      count: totalAttempted,
      color: STAGE_CONFIGS.attempted.borderColor,
      stageKey: 'attempted',
      isStart: true,
    },
  });

  // Column 2: Skipped, Uploaded
  let col2Index = 0;

  if (totalSkipped > 0) {
    const skippedId = 'skipped';
    nodes.push({
      id: skippedId,
      type: 'flowNode',
      position: { x: COL_SPACING, y: col2Index * ROW_SPACING },
      data: {
        label: 'Skipped',
        count: totalSkipped,
        color: STAGE_CONFIGS.skipped.borderColor,
        stageKey: 'skipped',
        isEnd: true,
      },
    });
    col2Index++;

    edges.push({
      id: `${attemptedId}-${skippedId}`,
      source: attemptedId,
      target: skippedId,
      style: { stroke: EDGE_COLOR, strokeWidth: 2 },
      animated: true,
    });
  }

  const uploadedId = 'uploaded';
  nodes.push({
    id: uploadedId,
    type: 'flowNode',
    position: { x: COL_SPACING, y: col2Index * ROW_SPACING },
    data: {
      label: 'Uploaded',
      count: totalUploaded,
      color: STAGE_CONFIGS.uploaded.borderColor,
      stageKey: 'uploaded',
    },
  });

  if (totalUploaded > 0) {
    edges.push({
      id: `${attemptedId}-${uploadedId}`,
      source: attemptedId,
      target: uploadedId,
      style: { stroke: EDGE_COLOR, strokeWidth: 2 },
      animated: true,
    });
  }

  // Column 3: Awaiting, Submitted, Deleted
  let col3Index = 0;

  if (stagedFiles > 0) {
    const stagedId = 'awaiting';
    nodes.push({
      id: stagedId,
      type: 'flowNode',
      position: { x: COL_SPACING * 2, y: col3Index * ROW_SPACING },
      data: {
        label: 'Awaiting Submission',
        count: stagedFiles,
        color: STAGE_CONFIGS.awaiting.borderColor,
        stageKey: 'awaiting',
        isEnd: true,
      },
    });
    col3Index++;

    edges.push({
      id: `${uploadedId}-${stagedId}`,
      source: uploadedId,
      target: stagedId,
      style: { stroke: EDGE_COLOR, strokeWidth: 2 },
      animated: true,
    });
  }

  let submittedId: string | null = null;
  if (submittedFiles > 0 || processed > 0 || notProcessed > 0) {
    submittedId = 'submitted';
    nodes.push({
      id: submittedId,
      type: 'flowNode',
      position: { x: COL_SPACING * 2, y: col3Index * ROW_SPACING },
      data: {
        label: 'Submitted',
        count: submittedFiles,
        color: submittedFiles > 0 ? STAGE_CONFIGS.submitted.borderColor : '#d1d5db',
        stageKey: 'submitted',
      },
    });
    col3Index++;

    if (submittedFiles > 0) {
      edges.push({
        id: `${uploadedId}-${submittedId}`,
        source: uploadedId,
        target: submittedId,
        style: { stroke: EDGE_COLOR, strokeWidth: 2 },
        animated: true,
      });
    }
  }

  const totalDeleted = deletionsByReason.staged + deletionsByReason.submitted;
  if (totalDeleted > 0) {
    const deletedId = 'deleted';
    nodes.push({
      id: deletedId,
      type: 'flowNode',
      position: { x: COL_SPACING * 2, y: col3Index * ROW_SPACING },
      data: {
        label: 'Deleted',
        count: totalDeleted,
        color: STAGE_CONFIGS.deleted.borderColor,
        stageKey: 'deleted',
        isEnd: true,
      },
    });
    col3Index++;

    edges.push({
      id: `${uploadedId}-${deletedId}`,
      source: uploadedId,
      target: deletedId,
      style: { stroke: EDGE_COLOR, strokeWidth: 2 },
      animated: true,
    });
  }

  // Column 4: Processed, Not Processed
  let col4Index = 0;

  if (processed > 0 && submittedId !== null) {
    const processedId = 'processed';
    nodes.push({
      id: processedId,
      type: 'flowNode',
      position: { x: COL_SPACING * 3, y: col4Index * ROW_SPACING },
      data: {
        label: 'Processed',
        count: processed,
        color: STAGE_CONFIGS.processed.borderColor,
        stageKey: 'processed',
        isEnd: true,
      },
    });
    col4Index++;

    edges.push({
      id: `${submittedId}-${processedId}`,
      source: submittedId,
      target: processedId,
      style: { stroke: EDGE_COLOR, strokeWidth: 2 },
      animated: true,
    });
  }

  if (notProcessed > 0 && submittedId !== null) {
    const notProcessedId = 'notProcessed';
    nodes.push({
      id: notProcessedId,
      type: 'flowNode',
      position: { x: COL_SPACING * 3, y: col4Index * ROW_SPACING },
      data: {
        label: 'Not Processed',
        count: notProcessed,
        color: STAGE_CONFIGS.notProcessed.borderColor,
        stageKey: 'notProcessed',
        isEnd: true,
      },
    });

    edges.push({
      id: `${submittedId}-${notProcessedId}`,
      source: submittedId,
      target: notProcessedId,
      style: { stroke: EDGE_COLOR, strokeWidth: 2 },
      animated: true,
    });
  }

  return { nodes, edges };
}

export function UploadFlowChart({
  matterId,
  allDocumentsCaseId,
  stats,
  onViewSkipped,
  onViewDeleted,
  linksEnabled = true,
}: UploadFlowChartProps) {
  const contextValue = useMemo<FlowChartContextValue>(
    () => ({
      matterId,
      allDocumentsCaseId,
      stats,
      onViewSkipped,
      onViewDeleted,
      linksEnabled,
    }),
    [matterId, allDocumentsCaseId, stats, onViewSkipped, onViewDeleted],
  );

  const flowData = useMemo(() => {
    return buildFlowData(stats);
  }, [stats]);

  if (!flowData) {
    return (
      <div>
        <div className="flex h-[200px] flex-col items-center justify-center text-gray-400">
          <Icon className="lucide--git-branch h-10 w-10 opacity-40" />
          <p className="mt-2 text-xs">No upload data yet</p>
        </div>
      </div>
    );
  }

  return (
    <FlowChartContext.Provider value={contextValue}>
      <div className="h-64 w-full">
        <ReactFlow
          nodes={flowData.nodes}
          edges={flowData.edges}
          nodeTypes={nodeTypes}
          proOptions={{ hideAttribution: true }}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          preventScrolling={false}
          minZoom={0.8}
          maxZoom={1}
          // Need a blank onclick to enable click handling on the nodes
          onNodeClick={() => {}}
        >
          <Background color="#f3f4f6" gap={16} />
        </ReactFlow>
      </div>
    </FlowChartContext.Provider>
  );
}
